// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Modal as PingModal } from '../../components/Modal';
import { createTheme } from '../../design-system/theme';
import { tasksStrings } from './strings';
import type { TaskFolderId, TaskItem, TaskPriority } from './types';

export interface NewTaskDraft {
  readonly title: string;
  readonly priority: TaskPriority;
  readonly dueAt: number | null;
  readonly folderId: TaskFolderId | null;
}

export interface NewTaskModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (draft: NewTaskDraft) => void;
  readonly initialFolderId?: TaskFolderId | null;
}

const PRIORITY_ORDER: readonly TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

/** Modal for creating a new task. Validation: non-empty title required. */
export const NewTaskModal: React.FC<NewTaskModalProps> = ({
  visible,
  onClose,
  onSubmit,
  initialFolderId,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');

  const handleSubmit = useCallback(() => {
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      return;
    }
    onSubmit({
      title: trimmed,
      priority,
      dueAt: null,
      folderId: initialFolderId ?? null,
    });
    setTitle('');
    setPriority('medium');
    onClose();
  }, [title, priority, initialFolderId, onSubmit, onClose]);

  const handleCancel = useCallback(() => {
    setTitle('');
    setPriority('medium');
    onClose();
  }, [onClose]);

  return (
    <PingModal
      visible={visible}
      onClose={handleCancel}
      accessibilityLabel={tasksStrings.newTaskTitle}
    >
      <View>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {tasksStrings.newTaskTitle}
        </Text>
        <Text style={[styles.label, { color: theme.colors.text.secondary }]}>
          {tasksStrings.taskTitleLabel}
        </Text>
        <TextInput
          value={title}
          onChangeText={setTitle}
          placeholder={tasksStrings.taskTitlePlaceholder}
          placeholderTextColor={theme.colors.text.tertiary}
          accessibilityLabel={tasksStrings.taskTitleLabel}
          autoFocus
          style={[
            styles.input,
            {
              color: theme.colors.text.primary,
              borderColor: theme.colors.border.primary,
              backgroundColor: theme.colors.surface.secondary,
            },
          ]}
        />
        <Text style={[styles.label, { color: theme.colors.text.secondary }]}>
          {tasksStrings.priorityLabel}
        </Text>
        <View style={styles.priorityRow}>
          {PRIORITY_ORDER.map((p) => {
            const selected = p === priority;
            return (
              <Pressable
                key={p}
                onPress={() => setPriority(p)}
                accessibilityRole="button"
                accessibilityLabel={tasksStrings.priority(p)}
                accessibilityState={{ selected }}
                style={({ pressed }) => [
                  styles.priorityChip,
                  {
                    borderColor: selected
                      ? theme.colors.accent.indigo
                      : theme.colors.border.secondary,
                    backgroundColor: selected ? `${theme.colors.accent.indigo}1A` : 'transparent',
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.priorityChipText,
                    {
                      color: selected ? theme.colors.accent.indigo : theme.colors.text.primary,
                    },
                  ]}
                >
                  {tasksStrings.priority(p)}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <View style={styles.actions}>
          <Pressable
            onPress={handleCancel}
            accessibilityRole="button"
            accessibilityLabel={tasksStrings.cancelCta}
            style={({ pressed }) => [styles.actionButton, pressed && { opacity: 0.7 }]}
          >
            <Text style={[styles.cancelText, { color: theme.colors.text.secondary }]}>
              {tasksStrings.cancelCta}
            </Text>
          </Pressable>
          <Pressable
            onPress={handleSubmit}
            accessibilityRole="button"
            accessibilityLabel={tasksStrings.saveCta}
            accessibilityState={{ disabled: title.trim().length === 0 }}
            disabled={title.trim().length === 0}
            style={({ pressed }) => [
              styles.actionButton,
              styles.primaryButton,
              { backgroundColor: theme.colors.accent.indigo },
              pressed && { opacity: 0.85 },
              title.trim().length === 0 && { opacity: 0.4 },
            ]}
          >
            <Text style={styles.primaryText}>{tasksStrings.saveCta}</Text>
          </Pressable>
        </View>
      </View>
    </PingModal>
  );
};

// Edit mode reuses the same modal layout with a pre-populated draft and an
// `onSubmit` that returns a partial update; see `EditTaskModal`.
export type { TaskItem };

const styles = StyleSheet.create({
  title: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 12,
  } satisfies TextStyle,
  label: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
    marginTop: 12,
    textTransform: 'uppercase',
  } satisfies TextStyle,
  input: {
    fontSize: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  } satisfies TextStyle,
  priorityRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  } satisfies ViewStyle,
  priorityChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    borderWidth: 1,
    marginRight: 6,
    marginBottom: 6,
  } satisfies ViewStyle,
  priorityChipText: {
    fontSize: 12,
    fontWeight: '600',
  } satisfies TextStyle,
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
  } satisfies ViewStyle,
  actionButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    marginLeft: 8,
  } satisfies ViewStyle,
  primaryButton: {
    minWidth: 80,
    alignItems: 'center',
  } satisfies ViewStyle,
  cancelText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  primaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  } satisfies TextStyle,
});
